/**
 * Contrato do RCS entre o painel e a API (task 17tm466frtx, `RCS-CONTRACT.md`).
 *
 * Dinheiro vem em micros inteiros (1 BRL = 1.000.000), como no resto da carteira,
 * e é formatado só na tela pelo `MoneyMicrosPipe`.
 */

/** Etapas da ficha do agente, na ordem em que a fila do Admin as percorre. */
export type RcsStage =
  | 'submitted'
  | 'technical_review'
  | 'carrier_review'
  | 'active'
  | 'rejected'
  | 'suspended';

/** Um agente por caso de uso: no Brasil o broker aceita um só (decisão D5). */
export type RcsUseCase = 'transactional' | 'promotional' | 'otp';

export interface RcsHistoryEntry {
  stage: RcsStage;
  /** ISO-8601. */
  at: string;
  note?: string | null;
  /** Acompanhamento (17tm466frtx): linha fina, além da mudança de `stage`. */
  step?: RcsProgressStep | null;
  event?: string | null;
}

/**
 * Etapa fina do acompanhamento (task 17tm466frtx, `RCS-ACOMP-CONTRACT.md`), separada da
 * `stage`: quem trava o envio continua sendo a `stage` (só `active` envia), e o
 * `progress_step` é só o que a tela de acompanhamento do cliente mostra.
 */
export type RcsProgressStep = 'submitted' | 'brand_verification' | 'google_review' | 'carrier_launch' | 'active';

/**
 * O cliente não vê operadora (29/10): o backend já manda os detalhes por operadora
 * como `releasing` (em liberação) e `active` (lançado em uma ou em todas).
 */
export type RcsProgressDetail =
  | 'awaiting_review'
  | 'awaiting_provider_link'
  | 'awaiting_verification_request'
  | 'awaiting_owner'
  | 'owner_confirmed'
  | 'verified'
  | 'releasing'
  | 'active'
  | 'provider_error';

export type RcsStepState = 'done' | 'current' | 'waiting' | 'todo' | 'failed';

/** Uma das 5 caixas da linha do tempo, já resolvida pelo backend (`GET /rcs/agents`). */
export interface RcsStepInfo {
  key: RcsProgressStep;
  label: string;
  state: RcsStepState;
  at: string | null;
  note: string | null;
}

export type RcsNotificationChannel = 'panel' | 'email' | 'whatsapp' | 'sms' | 'phone';
export type RcsNotificationBy = 'auto' | 'team' | 'customer';
export type RcsNotificationStatus = 'sent' | 'failed' | 'skipped' | 'delivered' | 'read';

export interface RcsAgentNotification {
  id: string;
  event: string;
  channel: RcsNotificationChannel;
  to: string | null;
  status: RcsNotificationStatus;
  sent_at: string;
  by: RcsNotificationBy;
  text: string;
}

export interface RcsEta {
  from: string;
  to: string;
}

export interface RcsAgent {
  id: string;
  name: string;
  color: string;
  cnpj: string;
  owner_name: string;
  owner_email: string;
  use_case: RcsUseCase;
  description: string;
  logo_url: string | null;
  banner_url: string | null;
  phone: string;
  email: string;
  website: string;
  terms_url: string;
  privacy_url: string;
  stage: RcsStage;
  rejection_reason: string | null;
  submitted_at: string | null;
  approved_at: string | null;
  history: RcsHistoryEntry[];
  can_send: boolean;

  // --- Acompanhamento (17tm466frtx) — opcionais: a ficha antiga (cadastro, duplicar)
  // não depende deles, só a tela "Agentes RCS" usa.
  progress_step?: RcsProgressStep;
  progress_detail?: RcsProgressDetail;
  /** Rótulo pt-BR já resolvido pelo backend; sem ele a tela usa o mapa local. */
  progress_label?: string | null;
  step_changed_at?: string | null;
  days_in_step?: number;
  awaiting_customer_since?: string | null;
  owner_confirmed_at?: string | null;
  resend_requested_at?: string | null;
  /** Fallback: `owner_email` quando a Pingoo não manda o contato da verificação. */
  verification_contact_email?: string | null;
  provider_checked_at?: string | null;
  eta?: RcsEta | null;
  steps?: RcsStepInfo[];
  notifications?: RcsAgentNotification[];
}

/** Recorte de `GET /api/rcs/agents/active`. */
export interface RcsActiveAgent {
  id: string;
  name: string;
  color: string;
  logo_url: string | null;
  use_case: RcsUseCase;
}

/** Campos de texto do cadastro. Logo e banner vão como arquivo ou por `copy_assets_from`. */
export interface RcsAgentDraft {
  name: string;
  color: string;
  cnpj: string;
  owner_name: string;
  owner_email: string;
  use_case: RcsUseCase | '';
  description: string;
  phone: string;
  email: string;
  website: string;
  terms_url: string;
  privacy_url: string;
}

export interface RcsMedia {
  id: string;
  url: string;
  title: string;
  category: string | null;
  width: number | null;
  height: number | null;
  size_bytes: number | null;
}

/** Botões da v1: abrir link, ligar e copiar código Pix (vira link da página de cópia, D2). */
export type RcsButtonType = 'url' | 'call' | 'pix';

export interface RcsButton {
  type: RcsButtonType;
  label: string;
  value: string;
}

export interface RcsContent {
  title: string;
  media_url: string | null;
  text: string;
  buttons: RcsButton[];
}

export type RcsMessageType = 'basic' | 'single';

/** `POST /api/rcs/preview`: a regra do backend é a que cobra, então é ela que manda na tela. */
export interface RcsPreview {
  type: RcsMessageType;
  reasons: string[];
  resolved_text: string;
  text_length: number;
  sms_text: string;
  sms_length: number;
  buttons: Array<{ type: RcsButtonType; label: string; resolved: string }>;
}

/** `POST /api/rcs/estimate`: custo em faixa, entre tudo SMS e tudo RCS (D3). */
export interface RcsEstimate {
  total: number;
  /** Números da lista que têm consulta de RCS no cache (30 dias). */
  checked: number;
  with_rcs: number;
  est_rcs: number;
  est_sms: number;
  type: RcsMessageType;
  price_rcs_micros: number;
  price_sms_micros: number;
  estimate_micros: number;
  min_micros: number;
  max_micros: number;
  wallet_balance_micros: number;
  enough: boolean;
  /** Nenhum broker da rota RCS do cliente tem a marca ativa: o envio seria recusado. */
  route_blocked?: boolean;
  route_message?: string | null;
}

export interface RcsBroadcastRequest {
  name: string;
  list_id: string;
  agent_id: string;
  content: RcsContent;
  scheduled_at?: string | null;
}

export type RcsFallbackReason = 'cache_no_rcs' | 'lookup_no_rcs' | 'lookup_failed' | 'rcs_refused' | 'invalid_phone' | 'monitored'
  // Decisão do Bruno (03/11/2026): RCS indisponível para a conta sai pelo SMS em vez de cancelar.
  | 'no_rcs_route' | 'rcs_no_funds' | 'internal_error' | 'rcs_timeout';

export interface RcsBroadcastStats {
  total: number;
  rcs: number;
  sms: number;
  /** "Por que virou SMS": só vem para o Admin (inclusive visualizando a conta do cliente). */
  reasons?: Record<RcsFallbackReason, number>;
  rcs_delivered: number;
  rcs_read: number;
  /** Com o retorno do broker, o clique do botão; sem ele, o do link curto (encurtador). */
  rcs_clicks: number;
  sms_delivered: number;
  sms_clicks: number;
  /**
   * Há retorno do broker (entregue/lido)? `false` (02/11/2026, `RCS_PROVIDER_WEBHOOKS_ENABLED`
   * desligado): o RCS conta como enviado no aceite e a tela não mostra entregues nem lidos.
   * Ausente (backend antigo) vale como `true`.
   */
  provider_status?: boolean;
  /** Quantos RCS levaram link curto (sem link, não há o que clicar). */
  rcs_links?: number;
}

/** Botão da prévia do relatório, com o destino de verdade (link, telefone, código Pix). */
export interface RcsBroadcastPreviewButton {
  type: RcsButtonType;
  label: string;
  target: string;
}

/** "Conteúdo enviado": a mensagem como o contato recebeu, com o contato de exemplo. */
export interface RcsBroadcastPreview {
  type: RcsMessageType;
  title: string | null;
  media_url: string | null;
  resolved_text: string;
  sms_text: string;
  sms_length: number;
  buttons: RcsBroadcastPreviewButton[];
}

export interface RcsBroadcastCharges {
  rcs_micros: number;
  refund_micros: number;
  sms_micros: number;
  /** SMS de fallback que saíram do pacote (conta com SMS fora da carteira). */
  sms_package_units?: number;
  total_micros: number;
}

export interface RcsBroadcast {
  id: string;
  name: string;
  status: string;
  type?: RcsMessageType | null;
  list_id?: string | null;
  list_name?: string | null;
  agent_id?: string | null;
  agent?: RcsActiveAgent | null;
  content?: RcsContent | null;
  scheduled_at?: string | null;
  created_at?: string | null;
  finished_at?: string | null;
  hold_micros?: number | null;
  /** A listagem traz só os totais (sem `stats`); o detalhe traz os dois. */
  total?: number | null;
  total_rcs?: number | null;
  total_sms?: number | null;
  stats?: RcsBroadcastStats | null;
  charges?: RcsBroadcastCharges | null;
  /** Só no detalhe; `null` quando o conteúdo não compõe (envio antigo). */
  preview?: RcsBroadcastPreview | null;
}

export type RcsMessageStatus = 'queued' | 'sent' | 'delivered' | 'read' | 'failed' | 'refused';

export interface RcsBroadcastMessage {
  msisdn_masked: string;
  channel: 'rcs' | 'sms';
  /** Só para o Admin: para o cliente a chave não vem. */
  fallback_reason?: RcsFallbackReason | null;
  status: RcsMessageStatus | string;
  clicked: boolean;
  at: string | null;
}

/** Filtros da tabela "Mensagens" do relatório (no backend: o envio pode ter milhares). */
export interface RcsBroadcastMessageFilters {
  phone?: string;
  /** Um `RcsMessageStatus` ou `clicked`. */
  status?: string;
  channel?: '' | 'rcs' | 'sms';
}

/** Paginação do Laravel, que é o que as rotas paginadas do contrato devolvem. */
export interface RcsPage<T> {
  data: T[];
  current_page: number;
  last_page: number;
  total: number;
  per_page?: number;
}

/** `GET /api/public/rcs/copy/{token}`: página pública do Pix, sem autenticação. */
export interface RcsCopyCode {
  brand: { name: string; color: string | null; logo_url: string | null };
  code: string;
  amount_micros: number | null;
  reference: string | null;
  expires_at: string | null;
}

/** Erro 422 do Laravel: mensagem geral e lista por campo. */
export interface RcsValidationErrors {
  message?: string;
  errors?: Record<string, string[]>;
}

// --- Rótulos de tela ----------------------------------------------------------

export const RCS_STAGE_LABELS: Record<RcsStage, string> = {
  submitted: 'Recebido',
  technical_review: 'Em análise pela SMS Funnel',
  carrier_review: 'Em aprovação no Google',
  active: 'Ativo',
  rejected: 'Reprovado',
  suspended: 'Suspenso',
};

/** Tom da pílula de etapa. O nome é do papel, a cor sai do token no SCSS. */
export const RCS_STAGE_TONES: Record<RcsStage, 'muted' | 'warm' | 'cool' | 'ok' | 'bad'> = {
  submitted: 'muted',
  technical_review: 'warm',
  carrier_review: 'cool',
  active: 'ok',
  rejected: 'bad',
  suspended: 'bad',
};

/** As quatro etapas reais da homologação, movidas pela fila do Admin (D6). */
export const RCS_STAGE_FLOW: RcsStage[] = ['submitted', 'technical_review', 'carrier_review', 'active'];

export const RCS_STEP_LABELS: Partial<Record<RcsStage, string>> = {
  submitted: 'Ficha recebida',
  technical_review: 'Análise da SMS Funnel',
  carrier_review: 'Aprovação do Google',
  active: 'Ativo para envio',
};

export const RCS_USE_CASES: Array<{ value: RcsUseCase; label: string; hint: string }> = [
  { value: 'transactional', label: 'Transacional', hint: 'Pedido, entrega, cobrança, segunda via. Sem oferta.' },
  { value: 'promotional', label: 'Promocional', hint: 'Ofertas, cupons e campanhas.' },
  { value: 'otp', label: 'Código de acesso', hint: 'Só códigos de verificação e login.' },
];

export const RCS_BUTTON_TYPES: Array<{ value: RcsButtonType; label: string; valueLabel: string; icon: string }> = [
  { value: 'url', label: 'Abrir link', valueLabel: 'Link', icon: 'fa-external-link' },
  { value: 'call', label: 'Ligar', valueLabel: 'Telefone', icon: 'fa-phone' },
  { value: 'pix', label: 'Copiar código Pix', valueLabel: 'Código Pix ou variável', icon: 'fa-clone' },
];

export const RCS_FALLBACK_REASON_LABELS: Record<RcsFallbackReason, string> = {
  cache_no_rcs: 'Número sem RCS, consultado nos últimos 30 dias',
  lookup_no_rcs: 'Número sem RCS, consultado agora',
  lookup_failed: 'Consulta sem resposta: enviado por SMS por segurança',
  rcs_refused: 'RCS recusado pelo aparelho: estornado e reenviado por SMS',
  invalid_phone: 'Telefone inválido: segue a regra do SMS',
  monitored: 'Número de monitoramento: sempre por SMS',
  no_rcs_route: 'Sem rota RCS ativa para o agente (alerta enviado): enviado por SMS',
  rcs_no_funds: 'Sem saldo na carteira para o RCS: enviado por SMS',
  internal_error: 'Erro interno no envio RCS: enviado por SMS',
  rcs_timeout: 'RCS indisponível até o fim da janela da etapa (ou das rodadas da API): enviado por SMS',
};

export const RCS_MESSAGE_STATUS_LABELS: Record<string, string> = {
  queued: 'Na fila',
  sent: 'Enviado',
  delivered: 'Entregue',
  read: 'Lido',
  failed: 'Falhou',
  refused: 'Recusado',
};

/** Categorias da galeria, as mesmas do layout do Misael. */
export const RCS_MEDIA_CATEGORIES = [
  'Promoções',
  'PIX & Pagamentos',
  'E-commerce & Carrinho',
  'Logística & Entregas',
  'Notificações',
  'Institucional',
];

/** Medidas do Agent Builder: o broker recusa fora disso. */
export const RCS_LOGO_SPEC = { width: 224, height: 224, maxKb: 50 };
export const RCS_BANNER_SPEC = { width: 1440, height: 448, maxKb: 200 };

export const RCS_TEXT_MAX_LENGTH = 2000;
export const RCS_BASIC_MAX_LENGTH = 160;
export const RCS_SMS_MAX_LENGTH = 160;
export const RCS_BUTTON_LABEL_MAX = 25;

/** Condição por leitura da etapa RCS (F7.5): "se não ler em X h, enviar SMS". */
export interface RcsReadCondition {
  hours: number;
  sms_text: string;
}

/** `GET /api/rcs/settings` → `read_condition`. Desligada, o editor não mostra o campo. */
export interface RcsReadConditionSettings {
  enabled: boolean;
  min_hours: number;
  max_hours: number;
  sms_max_length: number;
}

/**
 * `GET /api/rcs/settings` → `broadcast_schedule`: até quando o envio em massa pode ser
 * agendado. A reserva é feita na criação e vale 7 dias na carteira; `max_minutes` já desconta
 * a folga do despacho e do acerto. Relativo a agora, para não depender do fuso do servidor.
 */
export interface RcsBroadcastScheduleSettings {
  max_days: number;
  max_minutes: number;
}

export interface RcsSettings {
  read_condition?: Partial<RcsReadConditionSettings>;
  /** Há retorno do broker (entregue/lido)? Ver `RcsBroadcastStats.provider_status`. */
  provider_status?: boolean;
  broadcast_schedule?: Partial<RcsBroadcastScheduleSettings>;
}

/** Sem resposta do backend, o mesmo teto que ele aplica hoje (7 dias menos 90 min de folga). */
export const RCS_BROADCAST_SCHEDULE_DEFAULT: RcsBroadcastScheduleSettings = {
  max_days: 7, max_minutes: 7 * 24 * 60 - 90,
};

export const RCS_READ_CONDITION_DISABLED: RcsReadConditionSettings = {
  enabled: false, min_hours: 1, max_hours: 72, sms_max_length: RCS_SMS_MAX_LENGTH,
};

/** O prazo que o protótipo usa ("se não leu em 2 h"). */
export const RCS_READ_CONDITION_DEFAULT_HOURS = 2;

export type RcsReadConditionStatus = 'pending' | 'processing' | 'sent' | 'skipped' | 'failed';

export const RCS_READ_CONDITION_STATUS_LABELS: Record<RcsReadConditionStatus, string> = {
  pending: 'Aguardando o prazo',
  processing: 'Conferindo a leitura',
  sent: 'Não leu no prazo: SMS enviado',
  skipped: 'SMS não enviado',
  failed: 'SMS não saiu',
};

/** Entrega do SMS da condição pelo DLR da operadora (`read_condition.sms_status`). */
export const RCS_READ_CONDITION_SMS_STATUS_LABELS: Record<string, string> = {
  queued: 'Na fila do provedor',
  sent_to_carrier: 'Enviado à operadora',
  delivered: 'Entregue',
  undelivered: 'Não entregue',
};
export const RCS_MAX_BUTTONS = 2;

// --- Acompanhamento dos agentes (17tm466frtx) ----------------------------------

/** As 5 etapas finas, na ordem em que a linha do tempo da ficha sempre mostra. */
export const RCS_PROGRESS_STEPS: RcsProgressStep[] = [
  'submitted', 'brand_verification', 'google_review', 'carrier_launch', 'active',
];

export const RCS_PROGRESS_STEP_LABELS: Record<RcsProgressStep, string> = {
  submitted: 'Enviado',
  brand_verification: 'Verificação da marca',
  google_review: 'Análise do Google',
  carrier_launch: 'Liberação',
  active: 'Ativo',
};

/** Rótulo pt-BR por `progress_detail`, usado quando o backend não manda `progress_label`. */
export const RCS_PROGRESS_DETAIL_LABELS: Record<RcsProgressDetail, string> = {
  awaiting_review: 'Em conferência pela SMS Funnel',
  awaiting_provider_link: 'Aguardando a Pingoo associar',
  awaiting_verification_request: 'Aguardando início da verificação',
  awaiting_owner: 'Aguardando sua aprovação',
  owner_confirmed: 'Aprovado por você, aguardando o Google',
  verified: 'Marca verificada',
  releasing: 'Em liberação',
  active: 'Ativo',
  provider_error: 'Erro ao consultar o provedor',
};

export type RcsPillTone = 'muted' | 'warm' | 'cool' | 'ok' | 'bad';

export const RCS_NOTIFICATION_CHANNEL_LABELS: Record<RcsNotificationChannel, string> = {
  panel: 'Painel', email: 'E-mail', whatsapp: 'WhatsApp', sms: 'SMS', phone: 'Ligação',
};

/** `GET/PUT /api/rcs/notify-prefs`. */
export interface RcsNotifyChannelEmail {
  enabled: boolean;
  addresses: string[];
  default_address?: string;
}

export interface RcsNotifyChannelToggle {
  enabled: boolean;
  locked?: boolean;
  available?: boolean;
  number?: string | null;
}

export interface RcsNotifyChannels {
  panel: RcsNotifyChannelToggle;
  email: RcsNotifyChannelEmail;
  whatsapp: RcsNotifyChannelToggle;
  sms: RcsNotifyChannelToggle;
}

export interface RcsNotifyEvents {
  action_required: boolean;
  reminder: boolean;
  step_changed: boolean;
  final: boolean;
}

export interface RcsNotifyPrefs {
  channels: RcsNotifyChannels;
  events: RcsNotifyEvents;
  locked_events: string[];
  reminder_days: number;
}

/** Até a primeira resposta do backend, nada aparece travado além do que o contrato garante. */
export const RCS_NOTIFY_PREFS_DEFAULT: RcsNotifyPrefs = {
  channels: {
    panel: { enabled: true, locked: true },
    email: { enabled: true, addresses: [] },
    whatsapp: { enabled: false, available: false, number: null },
    sms: { enabled: false, available: false, number: null },
  },
  events: {
    action_required: true, reminder: true, step_changed: true, final: true,
  },
  locked_events: ['action_required', 'final'],
  reminder_days: 2,
};

/** Rótulo de cada evento de aviso, na ordem da coluna "Quando avisar". */
export const RCS_NOTIFY_EVENT_LABELS: Array<{ key: keyof RcsNotifyEvents; label: string }> = [
  { key: 'action_required', label: 'Quando eu precisar agir (aprovar o e-mail da marca)' },
  { key: 'reminder', label: 'Lembrete a cada alguns dias enquanto eu não agir' },
  { key: 'step_changed', label: 'Quando o agente mudar de etapa' },
  { key: 'final', label: 'Ativo, reprovado ou suspenso' },
];

/** As 5 etapas de "Como é a aprovação", na coluna lateral (a 2ª é a única que depende do cliente). */
export const RCS_HOW_IT_WORKS: Array<{ step: RcsProgressStep; title: string; text: string; ownerDepends: boolean }> = [
  { step: 'submitted', title: 'Enviado', text: 'A SMS Funnel confere a ficha e cria o agente. Até 1 dia útil.', ownerDepends: false },
  {
    step: 'brand_verification', title: 'Verificação da marca',
    text: 'A ABR Telecom manda um e-mail ao dono da marca. Ele precisa clicar em "Aprovar".', ownerDepends: true,
  },
  { step: 'google_review', title: 'Análise do Google', text: 'O Google confere marca, logo e links. 2 a 5 dias úteis.', ownerDepends: false },
  {
    step: 'carrier_launch', title: 'Liberação',
    text: 'Com a aprovação do Google, o agente é liberado para envio. 3 a 7 dias úteis.', ownerDepends: false,
  },
  { step: 'active', title: 'Ativo', text: 'Pronto para envio em massa e automações.', ownerDepends: false },
];
